import { FC } from "react"
import { useTranslation } from "react-i18next"
import TextInput from "components/inputs/textInput"
import IconButton from "components/buttons/iconButton"
import Button from "components/inputs/button"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import { ReactComponent as BarcodeIcon } from "assets/icons/barcode.svg"
import { InputMaxLength } from "types"
import useLayout from "hooks/useLayout"
import { normalizeBarcode } from "@/utils/barcode"
import { setBarcodeScanHandler } from "@/utils/barcodeScanHandler"

interface BarcodeListInputProps {
    barcodes: string[]
    onChange: (barcodes: string[]) => void
}

const BarcodeListInput: FC<BarcodeListInputProps> = ({ barcodes, onChange }) => {
    const { t } = useTranslation()
    const { openDialog, closeDialog, addToast } = useLayout()

    const updateAt = (index: number, value: string) => {
        const next = [...barcodes]
        next[index] = value
        onChange(next)
    }

    const removeAt = (index: number) => {
        onChange(barcodes.filter((_, i) => i !== index))
    }

    const addRow = (value = "") => {
        onChange([...barcodes, value])
    }

    const applyScannedBarcode = (index: number | null, barcode: string) => {
        const normalized = normalizeBarcode(barcode)
        const alreadyUsed = barcodes.some((existing, i) => i !== index && normalizeBarcode(existing) === normalized)
        if (alreadyUsed) {
            addToast({ text: t("toastBarcodeAlreadyAdded"), colorScheme: "yellow" })
            closeDialog()
            return true
        }
        if (index === null) {
            onChange([...barcodes, normalized])
        } else {
            const next = [...barcodes]
            next[index] = normalized
            onChange(next)
        }
        closeDialog()
        return true
    }

    const openScanner = (index: number | null) => {
        setBarcodeScanHandler((barcode: string) => applyScannedBarcode(index, barcode))
        openDialog("scan_barcode")
    }

    return (
        <div className="flex flex-col gap-y-3 sm:col-span-2">
            <p className="text-xs text-gray-500">{t("helperBarcodes")}</p>
            {barcodes.map((barcode, index) => (
                <div key={index} className="flex items-end gap-2">
                    <div className="w-64 max-w-full min-w-0">
                        <TextInput
                            label={`${t("labelBarcode")} ${index + 1}`}
                            value={barcode}
                            onChange={(e) => updateAt(index, e.target.value)}
                            maxLength={InputMaxLength.short}
                            showCharacterCount={false}
                        />
                    </div>
                    <IconButton
                        className="shrink-0"
                        icon={<BarcodeIcon />}
                        onClick={() => openScanner(index)}
                    />
                    <IconButton
                        className="shrink-0"
                        icon={<CloseIcon />}
                        onClick={() => removeAt(index)}
                    />
                </div>
            ))}
            <div className="flex flex-col gap-2 md:flex-row md:flex-wrap">
                <Button wideOnMobile type="button" design="primary" onClick={() => addRow()}>
                    <AddIcon />
                    <span>{t("buttonAddBarcode")}</span>
                </Button>
                <Button wideOnMobile type="button" design="white" onClick={() => openScanner(null)}>
                    <BarcodeIcon />
                    <span>{t("buttonScanBarcode")}</span>
                </Button>
            </div>
        </div>
    )
}

export default BarcodeListInput
